import type { Board } from './Board';
import type { PuppyId, Color } from './types';

// Mirrors Hints.swift::getPlayableSquares.
// Side effect: writes scoreIfPlayable onto each square's data, matching iOS.
// Returns: { playableSquares, maxScore }.
export function getPlayableSquares(
  board: Board,
  currPuppy: PuppyId,
  currColor: Color,
  currCrazy: boolean,
  currIsFireHydrant: boolean,
): { playableSquares: number; maxScore: number } {
  let playables = 0;
  let max = 0;

  for (let i = 0; i < board.numSquares; i++) {
    const result = board.canPlacePuppy(i, currPuppy, currColor, currCrazy, currIsFireHydrant);
    let score = result.canPlace ? result.score : 0;
    if (score > 0) {
      const mega = board.doesMakeMegaPuppy(i, currPuppy, currColor);
      if (mega.makesMegaPuppy || mega.makesSuperMegaPuppy) {
        score += 100;
      }
    }
    board.squareAt(i).data.scoreIfPlayable = score;
    if (score > max) max = score;
    if (score > 0) playables++;
  }

  return { playableSquares: playables, maxScore: max };
}

// Cardinal directions [dRow, dCol] in suggestion priority order. The first
// breed goes Up; opposite-first ordering keeps breeds maximally separated.
const ARM_DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [-1, 0], // Up
  [1, 0], // Down
  [0, -1], // Left
  [0, 1], // Right
];

// Counts consecutive empty (no-puppy) cells from (row,col) stepping by
// (dRow,dCol), stopping at the first occupied cell or the board edge. Used to
// choose the more-open side when an arm bends at the edge.
function countOpenCells(
  board: Board,
  row: number,
  col: number,
  dRow: number,
  dCol: number,
): number {
  const horiz = board.numSquaresHoriz;
  const vert = board.numSquaresVert;
  let n = 0;
  let r = row + dRow;
  let c = col + dCol;
  while (r >= 0 && r < vert && c >= 0 && c < horiz) {
    if (board.squareAt(r * horiz + c).data.puppy !== null) break;
    n++;
    r += dRow;
    c += dCol;
  }
  return n;
}

// Suggests the next hint cell: extend the active breed's "arm" radiating from
// the fire hydrant, or open a new arm in the next free direction. Stateless —
// everything is derived from the board, so it needs no persistence and is
// consistent across reloads. Returns a cell index, or -1 when there is no
// hydrant or no placeable arm cell (the caller then falls back to score-based).
export function suggestBreedArmCell(
  board: Board,
  puppy: PuppyId,
  color: Color,
): number {
  const horiz = board.numSquaresHoriz;
  const vert = board.numSquaresVert;
  const inBounds = (r: number, c: number): boolean =>
    r >= 0 && r < vert && c >= 0 && c < horiz;

  // 1. Locate the hydrant.
  let hydrant = -1;
  for (let i = 0; i < board.numSquares; i++) {
    if (board.squareAt(i).data.isFireHydrant) {
      hydrant = i;
      break;
    }
  }
  if (hydrant < 0) return -1;
  const hRow = Math.floor(hydrant / horiz);
  const hCol = hydrant % horiz;

  // 2/3. Pick the active breed's direction: the arm it already owns (its claim
  // cell holds this breed), else the first free direction in priority order.
  let dir: readonly [number, number] | null = null;
  let firstFree: readonly [number, number] | null = null;
  for (const d of ARM_DIRECTIONS) {
    const cRow = hRow + d[0];
    const cCol = hCol + d[1];
    if (!inBounds(cRow, cCol)) continue;
    const claim = board.squareAt(cRow * horiz + cCol).data;
    if (claim.puppy === puppy) {
      dir = d;
      break;
    }
    if (claim.puppy === null && firstFree === null) firstFree = d;
  }
  if (dir === null) dir = firstFree;
  if (dir === null) return -1;

  // 4/5. Walk outward from the hydrant; bend toward the more-open perpendicular
  // side at the edge. Return the first empty, placeable cell.
  const visited = new Set<number>();
  let row = hRow;
  let col = hCol;
  let dRow = dir[0];
  let dCol = dir[1];
  for (let step = 0; step < board.numSquares; step++) {
    if (!inBounds(row + dRow, col + dCol)) {
      // Perpendicular turn options; ordered so ties favour the lower-index
      // direction (Left before Right, Up before Down).
      const turns: ReadonlyArray<readonly [number, number]> =
        dRow !== 0
          ? [
              [0, -1],
              [0, 1],
            ]
          : [
              [-1, 0],
              [1, 0],
            ];
      let best: readonly [number, number] | null = null;
      let bestOpen = -1;
      for (const t of turns) {
        const open = countOpenCells(board, row, col, t[0], t[1]);
        if (open > bestOpen) {
          bestOpen = open;
          best = t;
        }
      }
      if (best === null || bestOpen === 0) return -1;
      dRow = best[0];
      dCol = best[1];
      continue;
    }
    row += dRow;
    col += dCol;
    const idx = row * horiz + col;
    if (visited.has(idx)) return -1; // safety: closed loop
    visited.add(idx);
    if (
      board.squareAt(idx).data.puppy === null &&
      board.canPlacePuppy(idx, puppy, color, false, false).canPlace
    ) {
      return idx;
    }
  }
  return -1;
}

// Listed for forward-compat with the Plan 2 render layer.
// In Plan 1 these are no-ops.
export class Hints {
  hintSprites: unknown[] = [];

  stopHintAnimations(): void {
    // no-op in pure-logic build
  }
}
